import { Injectable, effect, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';
import { APP_ROUTES } from '../constants/app-routes.const';
import { flyToSettings } from '../../shared/utils/fly-to-settings';

/**
 * The moment between "the import finished" and "the user has been told where the knowledge base
 * went". The rail stays locked across it, so the padlocks and the knowledge-base icon leave with
 * the animation rather than blinking out from under the user while they read the card.
 *
 * Only a run that finished in front of the user counts: arriving on a workspace that was ready
 * long ago has nothing to hand over.
 */
@Injectable({ providedIn: 'root' })
export class OnboardingHandoffService {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);

  readonly pending = signal(false);
  /** The mascot's one line after the hand-off: what just happened, and where the library went. */
  readonly greeting = signal(false);
  /**
   * The hand-off has been given in this session. The rail and the gate stop reading the status
   * after that: /users/me is re-read on every navigation, and one stale answer carrying PENDING
   * would otherwise put the padlock and the knowledge-base icon back for a frame — or bounce the
   * user off the page they are opening.
   */
  readonly handedOver = signal(false);
  private sawRunning = false;
  private playing = false;
  /** The workspace the hand-off was given for: a second account signing in here has not had it. */
  private handedOverOrg: number | null = null;
  /** One hand-off per workspace: a status that flickers must not send the icon flying twice. */
  private readonly playedOrgs = new Set<number>();

  constructor() {
    effect(() => {
      const user = this.auth.currentUser();
      const orgId = user?.organisationId ?? null;
      // The app outlives a session — signing out and back in as someone else must not carry the
      // hand-off's verdict over, or the gate and the rail would open on a workspace still building.
      if (orgId !== this.handedOverOrg) {
        this.handedOverOrg = orgId;
        this.handedOver.set(false);
        this.sawRunning = false;
      }

      const status = user?.organisation?.onboardingStatus;
      if (status === 'PENDING' || status === 'FAILED') {
        this.sawRunning = true;
        return;
      }
      if (status === 'READY' && this.sawRunning) {
        this.sawRunning = false;
        // Nothing to click: the workspace finishing is the event, and the hand-off is how the user
        // learns it. Waiting for a button meant a run that finished while they were reading
        // something else left the rail locked with nobody to unlock it.
        void this.play();
      }
    });
  }

  /** The user has seen the hand-off; the product opens up. */
  complete(): void {
    this.pending.set(false);
    this.handedOver.set(true);
  }

  greet(): void {
    this.greeting.set(true);
  }

  dismissGreeting(): void {
    this.greeting.set(false);
  }

  /**
   * The hand-off itself: the rail holds its locked shape, the knowledge base flies out of it into
   * the settings gear, the product opens, and the mascot says where the library went.
   */
  async play(): Promise<void> {
    const orgId = this.auth.currentUser()?.organisationId;
    if (!orgId || this.playing || this.playedOrgs.has(orgId)) return;
    this.playing = true;
    this.playedOrgs.add(orgId);

    this.pending.set(true);
    // A beat to notice the rail before it gives the icon up.
    await new Promise(resolve => setTimeout(resolve, 900));
    try {
      // Both rails carry the anchor and only one of them is on screen; a hidden element measures
      // as a zero-sized box at the origin, which throws the tile from the corner of the screen.
      const railItem = Array.from(document.querySelectorAll<HTMLElement>('[data-kb-anchor]')).find(el => el.getClientRects().length > 0);
      // The flight only explains the move; losing it (a cancelled animation) must not leave the
      // rail locked with the hand-off already marked as played.
      if (railItem) await flyToSettings(railItem).catch(() => undefined);
      this.complete();
      await this.router.navigate([this.landingRoute()]);
      this.greet();
    } finally {
      this.playing = false;
    }
  }

  /**
   * Practice is where a fresh workspace is worth something — the documents just generated are what
   * the simulations read. A plan without it would only be bounced to the paywall, so it stays on
   * the dashboard.
   */
  private landingRoute(): string {
    const canPractise = this.auth.hasCapability('emailPractice') || this.auth.hasCapability('coldCallPractice');
    return canPractise ? APP_ROUTES.PLAYGROUND : APP_ROUTES.DASHBOARD;
  }
}
